import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
  Panel,
} from "reactflow";
import "reactflow/dist/style.css";
import { BookMarked, ArrowLeft, Save, Trash2, Sparkles, Info, Shapes } from "lucide-react";
import { clusterPositions } from "@/lib/clusterLayout";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  listCitations,
  listConnections,
  createConnection,
  updateConnection,
  deleteConnection,
  getMapLayout,
  saveMapLayout,
  listFolders,
  listTags,
  listStickers,
} from "@/lib/api";
import StickerNode from "@/components/stickers/StickerNode";
import StickerTray, { STICKER_DND_TYPE } from "@/components/stickers/StickerTray";
import { useMapStickers, isStickerNode } from "@/components/stickers/useMapStickers";
import AppNav from "@/components/AppNav";
import UserMenu from "@/components/UserMenu";
import { Button } from "@/components/ui/button";
import CitationInspector from "@/components/CitationInspector";
import { useCitationStyle } from "@/hooks/useCitationStyle";
import { useKeywordColors } from "@/hooks/useKeywordColors";
import { paletteToken, DEFAULT_NODE_COLOR } from "@/lib/palette";

// Custom citation node
function CitationNode({ data }) {
  const kws = (data.keywords || []).slice(0, 4);
  const extra = Math.max(0, (data.keywords || []).length - kws.length);
  const tok = paletteToken(data.color || DEFAULT_NODE_COLOR);
  return (
    <div
      data-testid={`map-node-${data.id}`}
      className="group px-4 py-3 rounded-2xl shadow-sm hover:shadow-md transition-all max-w-[260px] cursor-pointer relative border-2"
      style={{ backgroundColor: tok.bg, borderColor: tok.hex }}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-[#3B2350]"
        style={{ backgroundColor: tok.hex }}
      />
      <div
        className="text-xs uppercase tracking-wider font-semibold mb-1"
        style={{ color: tok.fg }}
      >
        {data.type || "article"} · {data.year || "n.d."}
      </div>
      <div className="font-serif-display text-sm font-semibold leading-snug text-[#3B2350] line-clamp-3">
        {data.title}
      </div>
      {data.authorSummary && (
        <div className="text-xs text-[#5E4B70] mt-1 italic line-clamp-1">{data.authorSummary}</div>
      )}
      {kws.length > 0 && (
        <div
          data-testid={`map-node-keywords-${data.id}`}
          className="mt-2 flex flex-wrap gap-1"
        >
          {kws.map((k) => {
            const kt = paletteToken((data.keywordColorMap && data.keywordColorMap[k.toLowerCase()]) || "forest");
            return (
              <span
                key={k}
                className="text-xs leading-none px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: kt.hex + "22", color: kt.fg, border: `1px solid ${kt.hex}55` }}
              >
                #{k}
              </span>
            );
          })}
          {extra > 0 && (
            <span className="text-xs leading-none px-1.5 py-0.5 rounded-full bg-white/60 text-[#5E4B70] border border-black/10">
              +{extra}
            </span>
          )}
        </div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-[#3B2350]"
        style={{ backgroundColor: tok.hex }}
      />
    </div>
  );
}

const nodeTypes = { citation: CitationNode, sticker: StickerNode };

function layoutNodes(citations, savedPositions, keywordColorMap = {}) {
  const positionMap = Object.fromEntries((savedPositions || []).map((p) => [p.citation_id, p]));
  const cols = Math.max(2, Math.ceil(Math.sqrt(citations.length)));
  const savedYs = citations.map((c) => positionMap[c.id]?.y).filter((y) => y !== undefined);
  const baseY = savedYs.length ? Math.max(...savedYs) + 220 : 40;
  let free = 0;
  return citations.map((c) => {
    const saved = positionMap[c.id];
    const authors = c.authors || [];
    const authorSummary =
      authors.length === 0
        ? "Unknown author"
        : authors.length <= 2
        ? authors.join("; ")
        : `${authors[0]} et al.`;
    return {
      id: c.id,
      type: "citation",
      deletable: false,
      position: saved
        ? { x: saved.x, y: saved.y }
        : { x: (free % cols) * 300 + 40, y: baseY + Math.floor(free++ / cols) * 180 },
      data: {
        id: c.id,
        title: c.title,
        year: c.year,
        type: c.type,
        color: c.color,
        authorSummary,
        keywords: c.keywords || [],
        keywordColorMap,
      },
    };
  });
}

export default function LiteratureMap() {
  const [citations, setCitations] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [inspectId, setInspectId] = useState(null);
  const [style] = useCitationStyle();
  const { map: keywordColorMap, refresh: refreshKeywordColors } = useKeywordColors();
  const positionsRef = useRef([]);
  const saveTimer = useRef(null);
  const wrapperRef = useRef(null);
  const [rf, setRf] = useState(null);
  const { toNode, addSticker, saveStickerPosition, removeSticker } = useMapStickers(setNodes);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, conns, layout, fs, ts, sts] = await Promise.all([
        listCitations(),
        listConnections(),
        getMapLayout(),
        listFolders(),
        listTags(),
        listStickers(),
      ]);
      setCitations(cs);
      setFolders(fs);
      setTags(ts);
      positionsRef.current = layout.positions || [];
      const ns = layoutNodes(cs, layout.positions, keywordColorMap);
      setNodes([...ns, ...sts.map(toNode)]);

      const explicitEdges = conns.map((c) => ({
        id: c.id,
        source: c.source_id,
        target: c.target_id,
        label: c.label || "",
        type: "smoothstep",
        animated: false,
        deletable: false,
        style: { stroke: "#C9286F", strokeWidth: 2 },
        labelStyle: { fill: "#C9286F", fontWeight: 600, fontSize: 11 },
        labelBgStyle: { fill: "#FFF5FA" },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#C9286F" },
      }));

      // Compute keyword-shared edges (undirected, dedup by unordered pair)
      const explicitPairs = new Set(
        conns.flatMap((c) => [`${c.source_id}::${c.target_id}`, `${c.target_id}::${c.source_id}`])
      );
      const keywordEdges = [];
      const seenPairs = new Set();
      for (let i = 0; i < cs.length; i++) {
        const a = cs[i];
        const aKw = (a.keywords || []).map((k) => k.toLowerCase());
        if (!aKw.length) continue;
        for (let j = i + 1; j < cs.length; j++) {
          const b = cs[j];
          const bKw = new Set((b.keywords || []).map((k) => k.toLowerCase()));
          const shared = aKw.filter((k) => bKw.has(k));
          if (shared.length === 0) continue;
          const pairKey = `${a.id}::${b.id}`;
          if (seenPairs.has(pairKey)) continue;
          if (explicitPairs.has(pairKey)) continue; // don't duplicate an explicit connection
          seenPairs.add(pairKey);
          keywordEdges.push({
            id: `kw::${a.id}::${b.id}`,
            source: a.id,
            target: b.id,
            label: shared.length === 1 ? `#${shared[0]}` : `#${shared[0]} +${shared.length - 1}`,
            type: "smoothstep",
            animated: false,
            selectable: false,
            deletable: false,
            style: { stroke: "#8B5CF6", strokeWidth: 1.5, strokeDasharray: "6 5", opacity: 0.7 },
            labelStyle: { fill: "#8B5CF6", fontWeight: 600, fontSize: 10 },
            labelBgStyle: { fill: "#FFF5FA" },
            data: { keyword: true, sharedKeywords: shared },
          });
        }
      }

      setEdges([...keywordEdges, ...explicitEdges]);
    } finally {
      setLoading(false);
    }
  }, [setNodes, setEdges, keywordColorMap, toNode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const persistPositions = useCallback((currentNodes) => {
    const positions = currentNodes.filter((n) => !isStickerNode(n)).map((n) => ({ citation_id: n.id, x: n.position.x, y: n.position.y }));
    positionsRef.current = positions;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveMapLayout(positions).catch(() => {});
    }, 600);
  }, []);

  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
      // Save positions after drag
      const hasPositionChange = changes.some((c) => c.type === "position" && !c.dragging);
      if (hasPositionChange) {
        setNodes((current) => {
          persistPositions(current);
          return current;
        });
      }
    },
    [onNodesChange, persistPositions, setNodes]
  );

  const onConnect = useCallback(
    async (params) => {
      if (params.source === params.target) return toast.error("Can't connect a paper to itself");
      try {
        const created = await createConnection(params.source, params.target, "");
        setEdges((eds) =>
          addEdge(
            {
              ...params,
              id: created.id,
              label: created.label,
              type: "smoothstep",
              deletable: false,
              style: { stroke: "#C9286F", strokeWidth: 2 },
              labelStyle: { fill: "#C9286F", fontWeight: 600, fontSize: 11 },
              labelBgStyle: { fill: "#FFF5FA" },
              markerEnd: { type: MarkerType.ArrowClosed, color: "#C9286F" },
            },
            eds
          )
        );
        toast.success("Connection created");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to connect");
      }
    },
    [setEdges]
  );

  const onEdgeClick = useCallback((_, edge) => {
    if (edge?.data?.keyword) return; // keyword edges are not editable
    setSelectedEdge(edge);
  }, []);

  const onNodeClick = useCallback((_, node) => {
    if (isStickerNode(node)) return;
    // Open the citation inspector so the user can view/edit notes right on the map
    setInspectId(node.id);
  }, []);

  const onNodeDragStop = useCallback(
    (_, node) => {
      if (isStickerNode(node)) saveStickerPosition(node);
    },
    [saveStickerPosition]
  );

  const addStickerAtCenter = useCallback(
    (kind) => {
      if (!rf || !wrapperRef.current) return;
      const b = wrapperRef.current.getBoundingClientRect();
      const j = () => (Math.random() - 0.5) * 160;
      addSticker(kind, rf.screenToFlowPosition({ x: b.left + b.width / 2 + j(), y: b.top + b.height / 2 + j() }));
    },
    [rf, addSticker]
  );

  const onDrop = useCallback(
    (e) => {
      const kind = e.dataTransfer.getData(STICKER_DND_TYPE);
      if (!kind || !rf) return;
      e.preventDefault();
      addSticker(kind, rf.screenToFlowPosition({ x: e.clientX, y: e.clientY }));
    },
    [rf, addSticker]
  );

  const clusterByKeyword = useCallback(() => {
    const prev = Object.fromEntries(nodes.filter((n) => !isStickerNode(n)).map((n) => [n.id, n.position]));
    const apply = (map) => {
      setNodes((ns) => {
        const next = ns.map((n) => (map[n.id] ? { ...n, position: map[n.id] } : n));
        persistPositions(next);
        return next;
      });
      setTimeout(() => rf?.fitView({ padding: 0.35, duration: 500 }), 60);
    };
    apply(clusterPositions(citations));
    toast.success("Papers grouped by shared keywords", {
      duration: 8000,
      action: { label: "Undo", onClick: () => apply(prev) },
    });
  }, [nodes, citations, setNodes, persistPositions, rf]);

  const handleCitationUpdate = useCallback(
    (updated) => {
      setCitations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      // Refresh keyword colours in case a new keyword was added
      refreshKeywordColors();
      // Keep the map node's data (colour, keyword chips, title etc.) in sync
      setNodes((current) =>
        current.map((n) =>
          n.id === updated.id
            ? {
                ...n,
                data: {
                  ...n.data,
                  title: updated.title,
                  year: updated.year,
                  type: updated.type,
                  color: updated.color,
                  keywords: updated.keywords || [],
                  keywordColorMap,
                  authorSummary:
                    (updated.authors || []).length === 0
                      ? "Unknown author"
                      : (updated.authors || []).length <= 2
                      ? (updated.authors || []).join("; ")
                      : `${(updated.authors || [])[0]} et al.`,
                },
              }
            : n
        )
      );
    },
    [setNodes, keywordColorMap, refreshKeywordColors]
  );

  const handleLabelSave = async (newLabel) => {
    if (!selectedEdge) return;
    try {
      await updateConnection(selectedEdge.id, newLabel);
      setEdges((eds) =>
        eds.map((e) => (e.id === selectedEdge.id ? { ...e, label: newLabel } : e))
      );
      toast.success("Label updated");
      setSelectedEdge(null);
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleLabelDelete = async () => {
    if (!selectedEdge) return;
    try {
      await deleteConnection(selectedEdge.id);
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));
      toast.success("Connection removed");
      setSelectedEdge(null);
    } catch {
      toast.error("Failed to delete");
    }
  };

  const stats = useMemo(
    () => ({ nodes: citations.length, edges: edges.length }),
    [citations.length, edges.length]
  );

  return (
    <div className="h-screen flex flex-col bg-[#FFF5FA] dark:bg-[#1E1529]" data-testid="literature-map-page">
      <header className="border-b border-[#F5D0E3] dark:border-[#45325A] bg-[#FFF5FA]/85 dark:bg-[#1E1529]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-4 flex items-center gap-4 overflow-x-auto">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#F472B6] to-[#A78BFA] text-white grid place-items-center shadow-[0_4px_14px_rgba(244,114,182,0.45)] -rotate-6">
              <BookMarked className="w-5 h-5" />
            </div>
            <div>
              <div className="font-serif-display text-xl font-semibold leading-none text-[#3B2350] dark:text-[#FBEFF6]">
                Literature Map
              </div>
              <div className="text-xs text-[#7A6A8C] dark:text-[#8E7BA6] tracking-wide mt-1 uppercase">
                {stats.nodes} papers · {stats.edges} connections
              </div>
            </div>
          </div>
          <div className="shrink-0"><AppNav /></div>
          <div className="flex-1" />
          <div className="hidden lg:flex items-center gap-4 text-xs text-[#7A6A8C] dark:text-[#8E7BA6]">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-6 h-[2px] bg-[#C9286F]" /> your connection
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-6 h-[2px] border-t-2 border-dashed border-[#8B5CF6]" /> shared keyword
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-[#7A6A8C] dark:text-[#8E7BA6] px-3 py-1.5 rounded-lg bg-[#FCE7F3] dark:bg-[#2E2140]">
            <Info className="w-3.5 h-3.5" />
            Click a paper to edit notes · drag to connect · add stickers
          </div>
          <UserMenu />
        </div>
      </header>

      <div className="flex-1 relative" ref={wrapperRef}>
        {loading ? (
          <div className="h-full grid place-items-center text-[#7A6A8C]">Loading map…</div>
        ) : citations.length === 0 ? (
          <div className="h-full grid place-items-center px-6">
            <div className="text-center max-w-md fade-slide-in">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#C9286F] dark:text-[#C4A1FF] font-semibold mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Nothing to map yet
              </div>
              <h2 className="font-serif-display text-4xl text-[#3B2350] dark:text-[#FBEFF6]">
                Add citations to see them here.
              </h2>
              <p className="text-sm text-[#5E4B70] dark:text-[#BBA9CF] mt-3">
                Once you have a few sources in your library, come back here to draw connections between them &mdash; think citation graph, but personal.
              </p>
              <Link to="/">
                <Button className="mt-6 bg-[#C9286F] hover:bg-[#B8246C] dark:bg-[#C4A1FF] dark:hover:bg-[#A982F0] text-white gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back to library
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            onNodeClick={onNodeClick}
            onNodeDragStop={onNodeDragStop}
            onNodesDelete={(ns) => ns.filter(isStickerNode).forEach((n) => removeSticker(n.data.stickerId))}
            onInit={setRf}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
          >
            <Panel position="top-right">
              <Button
                data-testid="cluster-by-keyword-btn"
                onClick={clusterByKeyword}
                className="bg-white hover:bg-[#FDF2F8] text-[#C9286F] border-2 border-[#F5D0E3] shadow-[0_6px_20px_rgba(201,40,111,0.12)] gap-2 font-semibold"
              >
                <Shapes className="w-4 h-4" /> Group by keyword
              </Button>
            </Panel>
            <Panel position="top-left">
              <StickerTray onAdd={addStickerAtCenter} />
            </Panel>
            <Background color="#F2BFD9" gap={24} size={1.5} />
            <Controls className="!bg-white dark:!bg-[#3B2350] !border !border-[#F5D0E3] dark:!border-[#45325A]" />
            <MiniMap
              className="!hidden sm:!block !bg-[#FDF2F8] dark:!bg-[#1A1224] !border !border-[#F5D0E3]"
              nodeColor={(n) => (isStickerNode(n) ? "#F9A8D4" : "#C9286F")}
              maskColor="rgba(251, 249, 245, 0.7)"
            />
          </ReactFlow>
        )}
      </div>

      {selectedEdge && (
        <EdgeLabelEditor
          edge={selectedEdge}
          onSave={handleLabelSave}
          onDelete={handleLabelDelete}
          onClose={() => setSelectedEdge(null)}
        />
      )}

      <CitationInspector
        citation={citations.find((c) => c.id === inspectId) || null}
        folders={folders}
        tags={tags}
        allCitations={citations}
        style={style}
        keywordColor={(name) => keywordColorMap[(name || "").toLowerCase()] || "forest"}
        onClose={() => setInspectId(null)}
        onUpdate={handleCitationUpdate}
      />
    </div>
  );
}

function EdgeLabelEditor({ edge, onSave, onDelete, onClose }) {
  const [label, setLabel] = useState(edge.label || "");
  const presets = ["cites", "extends", "contradicts", "supports", "replicates", "reviews"];
  return (
    <div
      data-testid="edge-label-editor"
      className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 bg-white dark:bg-[#3B2350] border border-[#F5D0E3] dark:border-[#45325A] shadow-xl rounded-2xl p-4 w-[420px] max-w-[92vw] fade-slide-in"
    >
      <div className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold mb-2">
        Label this connection
      </div>
      <div className="flex gap-2 mb-3">
        <input
          data-testid="edge-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSave(label)}
          placeholder="e.g., extends, contradicts, cites…"
          className="flex-1 h-9 px-3 rounded-md border border-[#F5D0E3] dark:border-[#45325A] bg-[#FFF5FA] dark:bg-[#1E1529] text-sm outline-none focus:border-[#C9286F]"
          autoFocus
        />
        <Button
          data-testid="edge-label-save"
          onClick={() => onSave(label)}
          className="bg-[#C9286F] hover:bg-[#B8246C] dark:bg-[#C4A1FF] dark:hover:bg-[#A982F0] text-white h-9 px-3 gap-1.5"
        >
          <Save className="w-3.5 h-3.5" /> Save
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {presets.map((p) => (
          <button
            key={p}
            onClick={() => setLabel(p)}
            className="text-xs px-2 py-1 rounded-full bg-[#FCE7F3] dark:bg-[#2E2140] text-[#5E4B70] dark:text-[#BBA9CF] hover:bg-[#F5D0E3] transition-colors"
          >
            {p}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs">
        <button onClick={onClose} className="text-[#7A6A8C] hover:text-[#3B2350] dark:hover:text-[#FBEFF6]">
          Cancel
        </button>
        <button
          data-testid="edge-label-delete"
          onClick={onDelete}
          className="text-[#C9286F] dark:text-[#C4A1FF] hover:underline flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" /> Delete connection
        </button>
      </div>
    </div>
  );
}
